import { Store } from 'redux';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { DisplayMode } from '../../ts/ServerApi/types/ISessionInfo';
import { updateExplorerDisplayMode, updateFilterPanelVisibility } from '../actions/DisplaySettingsActions';
import { selectDisplaySettings } from '../selectors/DisplaySettingsSelectors';
import { AppState } from '../state/AppState';
import { DisplaySettings } from '../state/DisplaySettingsState';

export interface DisplaySettingsScope extends ng.IScope {
  displaySettings: DisplaySettings;
}

/**
 * This function adds Redux bindings for "display settings" onto the given scope, allowing
 * these settings to be kept in sync across both Angular and React components.
 *
 * The ultimate source of truth for these settings is the Redux store. This class allows interested
 * Angular controllers to subscribe to the Redux store and update their scope to stay in sync with
 * those values. Conversely, watchers are set up on the Angular scope that dispatch Redux actions
 * whenever the display settings change from within Angular code.
 *
 * Once all the Angular code that depends on "displaySettings" has been removed, these bindings will
 * no longer be necessary and can be deleted.
 */
export function bindDisplaySettingsScopeToReduxAndSetInitialValues(
    $scope: DisplaySettingsScope,
    store: Store<AppState>
): void {
  $scope.displaySettings = copyDisplaySettingsFromRedux(store);

  setUpStoreSubscription($scope, store);
  setUpAngularWatchers($scope, store);
}

function setUpStoreSubscription($scope: DisplaySettingsScope, store: Store<AppState>) {

  /*
   * It's possible that Redux actions to update the display settings happen within Angular digest
   * cycles (as is the case with the $locationChangeSuccess callback in the URL bindings). To handle
   * this, we must apply the result to the scope outside the digest, using $applyAsync.
   *
   * Since the Redux store subscription gets invoked on EVERY Redux store update, it's important to
   * invoke Angular's apply/digest only if the display settings have actually changed. We do this by
   * comparing the new Redux state against the previous Redux state with a memoized function (rather
   * than comparing the new Redux state against the current value of the scope, because another
   * state update may have arrived before the previous one was asynchronously applied).
   */
  const applyDisplaySettingsToScopeIfTheyHaveChanged = defaultMemoize(applyDisplaySettingsToScope, _.isEqual);

  function handleStateUpdate(store: Store<AppState>, $scope: DisplaySettingsScope) {
    const displaySettings = copyDisplaySettingsFromRedux(store);
    applyDisplaySettingsToScopeIfTheyHaveChanged($scope, displaySettings);
  }

  const unsubscribeFromStore = store.subscribe(() => handleStateUpdate(store, $scope));
  $scope.$on('$destroy', unsubscribeFromStore);

  // Run the handler once initially to prime the memoizer
  handleStateUpdate(store, $scope);
}

// If the display settings object is not explicitly copied, the Redux state ends up being modified
// through angular data binding (since selectDisplaySettings returns a reference to an object in the
// state).
export function copyDisplaySettingsFromRedux(store: Store<AppState>) {
  return _.clone(selectDisplaySettings(store.getState()));
}

function applyDisplaySettingsToScope($scope: DisplaySettingsScope, displaySettings: DisplaySettings) {
  $scope.$applyAsync(() => $scope.displaySettings = displaySettings);
}

function setUpAngularWatchers($scope: DisplaySettingsScope, store: Store<AppState>) {
  $scope.$watch(
    () => $scope.displaySettings.explorerDisplayMode,
    (newValue: DisplayMode, oldValue: DisplayMode) => {
      if (newValue !== oldValue) {
        store.dispatch(updateExplorerDisplayMode(newValue, true));
      }
    }
  );

  $scope.$watch(
    () => $scope.displaySettings.showFilterPanel,
    (newValue: boolean, oldValue: boolean) => {
      if (newValue !== oldValue) {
        store.dispatch(updateFilterPanelVisibility(newValue));
      }
    }
  );
}
